/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { eventUtil, Modal, Heading, Types, Messages, RichText } from '@marriott/mi-ui-library';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import {
  AccountModalEvent,
  ACCOUNT_MODAL_CLOSE_EVENT,
  ACCOUNT_MODAL_OPEN_EVENT,
  FLYOUT_DRAWER_EVENTS,
  addClassToBlankTargetLinks,
  CHECKED_IN_MODAL_ID,
  modalScrollHandler,
  setSubDirectoryPrefix,
  NEW_PASSWORD_FORM,
  flyoutModalKeys,
  FORGOT_PASSWORD_FORM,
} from '../../modules/utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { replaceText } from '../../modules/utils/promotionHelper';
import { useAccountModelStore } from '../../modules/store/accountModelStore';
import { fetchLinkJson } from '../../modules/utils/flyoutHelper';
import { StyledModalAccountContainerDiv, StyledFlyoutDrawerContainerDiv } from './AccountContainer.styles';

const AccountContainerV2 = dynamic(() =>
  import('../AccountContainer/AccountContainerV2').then(mod => mod.AccountContainerV2)
);
/**
 *
 * @param param0
 * will recive a prop Children = value should be a function component that will have some props
 * @returns
 * modal component with required children componenet
 */

export const MemoizedPageContentWithModal: FC<{
  Children: FC<{ accountEvent?: AccountModalEvent | null }>;
  modalId?: string;
  isFlyoutDrawer?: boolean;
  customerSupportCTALabel?: string;
  backToSignInCTALabel?: string;
  enablePhaseOne?: boolean;
}> = ({ Children, modalId, isFlyoutDrawer, backToSignInCTALabel, customerSupportCTALabel, enablePhaseOne }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [accountModalEvent, setAccountModalEvent] = useState<AccountModalEvent | null>();
  const accountContainerModalIdClass = modalId ? `account-container-modal__${modalId}` : '';
  const flyoutContainerModalIdClass = modalId ? `flyout-container-modal__${modalId}` : '';
  const {
    activeKey,
    setActiveKey,
    isContentLoading,
    setIsContentLoading,
    modelData,
    setModelData,
    setTokenExpiredFlyout,
    setPageErrorReset,
  } = useAccountModelStore(state => state);

  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { ACCEPT_LANGUAGE: currentLocale } = clientEnvVars;

  const clearAllStateOnClose = () => {
    modalScrollHandler(false);
    setIsModalOpen(false);
    setAccountModalEvent(null);
  };

  const handleModalCloseReset = () => {
    setActiveKey(null);
    setPageErrorReset(true);
  };

  const fetchDataPageResponse = async () => {
    setIsContentLoading(true);
    const modelDataPageResponse = await fetchLinkJson(NEW_PASSWORD_FORM, {
      'accept-language': currentLocale ?? 'en-US',
    });
    if (modelDataPageResponse) {
      // set token expired for forgot password component
      modelDataPageResponse?.modalKey === FORGOT_PASSWORD_FORM && setModelData(modelDataPageResponse);
      setActiveKey(
        modelDataPageResponse?.modalKey === NEW_PASSWORD_FORM
          ? flyoutModalKeys?.NEW_PASSWORD_FORM
          : flyoutModalKeys?.ACTIVATE_OR_FORGOT_FORM
      );
      setModelData(modelDataPageResponse);
      setTokenExpiredFlyout(modelDataPageResponse?.['tokenExpired']);
      setIsContentLoading(false);
    } else {
      setIsContentLoading(false);
    }
  };

  useEffect(() => {
    /**register modal for now
     * TODO: need to integrate this with zustand
     */

    eventUtil.on(ACCOUNT_MODAL_OPEN_EVENT, (modalEvent: AccountModalEvent) => {
      /** open modal and publish the event
       * listen data if anything publish
       */
      if (modalId === modalEvent?.modalType) {
        setSubDirectoryPrefix(modalEvent?.subDirectoryPrefix ?? '');
        setIsModalOpen(true);
        setAccountModalEvent(modalEvent);
        if (isFlyoutDrawer) {
          /** trigger flyout open event
           * if we got the flyout:true on events
           */
          eventUtil.dispatch(FLYOUT_DRAWER_EVENTS.OPEN, {
            contentWrapperClass: clsx('px-md-4 pt-2 pb-5 container-modal-v2'),
            flyoutCompId: modalEvent?.modalType,
          });
          const isNewPwrdForm = modalEvent?.modalId === NEW_PASSWORD_FORM;
          isNewPwrdForm && fetchDataPageResponse();
        }
      } else {
        modalScrollHandler(true);
      }
    });
    eventUtil.on(ACCOUNT_MODAL_CLOSE_EVENT, enablePhaseOne ? clearAllStateOnClose : handleModalCloseReset);
    eventUtil.on(FLYOUT_DRAWER_EVENTS.close, enablePhaseOne ? clearAllStateOnClose : handleModalCloseReset);
    return () => {
      /**remove registered event */
      eventUtil.remove(ACCOUNT_MODAL_CLOSE_EVENT, enablePhaseOne ? clearAllStateOnClose : handleModalCloseReset);
      eventUtil.remove(FLYOUT_DRAWER_EVENTS.close, enablePhaseOne ? clearAllStateOnClose : handleModalCloseReset);
      eventUtil.remove(ACCOUNT_MODAL_OPEN_EVENT, enablePhaseOne ? clearAllStateOnClose : handleModalCloseReset);
    };
  }, []);

  const closeModal = () => {
    //event dispatch close modal event, empty modal event obj
    if (document.body) {
      document.body.style.removeProperty('overflow');
    }
    eventUtil.dispatch(ACCOUNT_MODAL_CLOSE_EVENT, () => {});
    setIsModalOpen(false);
    setActiveKey(null);
  };

  if (isFlyoutDrawer) {
    return (
      <StyledFlyoutDrawerContainerDiv
        className={`account-flyout-container ${flyoutContainerModalIdClass}`}
        id={modalId}
      >
        {isModalOpen && isContentLoading && (
          <div className="spinner-wrapper d-flex justify-content-center align-items-center">
            <div className="m-spinner-m spinner-loader"></div>
          </div>
        )}
        {isModalOpen &&
          !isContentLoading &&
          (enablePhaseOne && activeKey && modelData ? (
            <AccountContainerV2
              modelData={modelData}
              backToSignInCTALabel={backToSignInCTALabel}
              customerSupportCTALabel={customerSupportCTALabel}
              enablePhaseOne={enablePhaseOne}
            />
          ) : (
            <Children accountEvent={accountModalEvent} />
          ))}
      </StyledFlyoutDrawerContainerDiv>
    );
  }
  return (
    <StyledModalAccountContainerDiv>
      <Modal
        className={clsx(`account-container-modal custom-scrollbar`, isModalOpen && 'show')}
        secondaryClassName={`account-container-modal-secondary standard ${accountContainerModalIdClass}`}
        show={isModalOpen}
        handleBlur={true}
        popupOpenState={isModalOpen}
        setPopupOpenState={() => {
          closeModal();
        }}
        role="dialog"
        labelledBy="account-container-modal"
        onClose={closeModal}
        modalId="account-container-modal"
      >
        <Modal.Header
          className="modal-header pr-4 pt-4 pb-4 p-lg-0 d-flex justify-content-end"
          labelText=""
          popupHeaderOnCLoseFunc={closeModal}
        />
        {isModalOpen && (
          <Modal.Body className={clsx('py-0 modal-body px-md-5', modalId === CHECKED_IN_MODAL_ID ? 'px-4' : 'px-3')}>
            <Children accountEvent={accountModalEvent} />
          </Modal.Body>
        )}
      </Modal>
    </StyledModalAccountContainerDiv>
  );
};

export const MemoizedPageTitleAndAlertContent: React.FC<{
  isEAASignIn?: boolean;
  contentDescription?: string;
  title?: string;
  pageErrors?: any;
  displayTypeOneError?: boolean;
  typeTwoErrorHeader?: string;
  isOverlay?: boolean;
  pagePath?: string;
  isCheckedIn?: boolean;
  trackingPageName: string;
  enablePhaseOne?: boolean;
}> = ({
  isEAASignIn,
  isOverlay,
  contentDescription,
  title,
  pageErrors,
  displayTypeOneError,
  typeTwoErrorHeader,
  isCheckedIn,
  trackingPageName,
  enablePhaseOne,
}) => {
  /** memoized the component due to avoid the rerender
   * if any state update then it will rerender only
   */
  return (
    <div
      className={clsx(
        {
          'row mb-4':
            pageErrors?.text ||
            !!pageErrors?.errorList?.length ||
            (!isEAASignIn && (title || contentDescription || pageErrors)) ||
            enablePhaseOne,
        },
        isCheckedIn && 'mb-md-5'
      )}
    >
      {title && (
        <Heading
          fontSize={Types.size.small}
          variation={Types.headingType.title}
          titleText={title}
          customClass="col-12 page-title mb-0 t-title-s px-2"
        />
      )}
      {contentDescription && (
        <Heading
          variation={Types.headingType.body}
          titleText={contentDescription}
          customClass="col-12 t-font-s mt-1 px-2"
        />
      )}
      {(pageErrors?.text || !!pageErrors?.errorList?.length) && (
        <div className="col-12 error_container">
          <Messages
            messageType="error-sev1"
            className={clsx('mt-3 account-page-error-msg px-2', isOverlay ? 'overlay-msg-content' : '')}
          >
            {pageErrors?.text && (
              <div role="alert">
                <RichText
                  text={addClassToBlankTargetLinks(replaceText(pageErrors?.text || '', [trackingPageName || '']))}
                  componentId="page-errors"
                />
              </div>
            )}
            {pageErrors?.errorList &&
              !!pageErrors?.errorList?.length &&
              (displayTypeOneError ? (
                <div role="alert">
                  <RichText text={pageErrors?.errorList[0]} componentId="type-one-error" />
                </div>
              ) : (
                <>
                  <span>{typeTwoErrorHeader}</span>
                  {pageErrors?.errorList.map((pe: string) => (
                    <ul>
                      <li>{pe}</li>
                    </ul>
                  ))}
                </>
              ))}
          </Messages>
        </div>
      )}
    </div>
  );
};
